import { CommonModule } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewChild,
  inject,
} from '@angular/core';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { Clipboard } from '@freelancer/clipboard';
import { ScreenSizeService } from '@freelancer/screen-size';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconModule, IconSize } from '@freelancer/ui/icon';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import type { Observable } from 'rxjs';
import {
  ClipboardFieldType,
  buttonTooltipText,
  TooltipType,
  PASSWORD_MASK,
} from './clipboard-field.types';

@Component({
  standalone: true,
  selector: 'app-clipboard-field',
  imports: [
    CommonModule,
    UiModule,
    IconModule,
    MatTooltipModule,
    TrackingModule,
  ],
  template: `
    <div
      #tooltip="matTooltip"
      class="Container"
      [matTooltip]="ButtonTooltipText[TooltipType.COPY]"
      [matTooltipDisabled]="isMobile$ | flAsync"
      [matTooltipPosition]="'above'"
    >
      <div class="CopyField">
        <div
          class="CopyField-textWrapper"
          flTrackingLabel="CopyFieldClicked"
          (click)="onCopyFieldClick()"
          (copy)="onCopyFieldCopy()"
          (mouseenter)="showTooltip(TooltipType.COPY)"
          (mouseleave)="tooltip.hide()"
        >
          <p
            class="CopyField-textWrapper-text text-body-16"
            [flMaxLines]="1"
          >
            {{ isVisible ? value : PASSWORD_MASK }}
          </p>
        </div>

        @if (type === 'password') {
          <fl-button
            class="Button"
            flTrackingLabel="TogglePasswordVisibilityClicked"
            [color]="ButtonColor.TEXT_DARK"
            [label]="'Toggle password visibility button'"
            [size]="ButtonSize.MID"
            (click)="onVisibilityToggle($event)"
            (mouseenter)="
              this.isVisible
                ? showTooltip(TooltipType.HIDE)
                : showTooltip(TooltipType.SHOW)
            "
            (mouseleave)="tooltip.hide()"
          >
            <fl-icon
              [name]="this.isVisible ? 'ui-visibility-off' : 'ui-visibility'"
              [size]="IconSize.SMALL"
              [useIconFont]="true"
              [fillIconFont]="true"
            ></fl-icon>
          </fl-button>
        }

        @if (type === 'link') {
          <fl-button
            class="Button"
            flTrackingLabel="OpenExternalLinkClicked"
            [color]="ButtonColor.TEXT_DARK"
            [label]="'Open link in a new tab button'"
            [link]="value"
            [newTab]="true"
            [size]="ButtonSize.MID"
            (mouseenter)="showTooltip(TooltipType.OPEN_LINK)"
            (mouseleave)="tooltip.hide()"
          >
            <fl-icon
              [name]="'ui-open-external'"
              [size]="IconSize.SMALL"
              [useIconFont]="true"
              [fillIconFont]="true"
            ></fl-icon>
          </fl-button>
        }
      </div>

      <fl-button
        class="Button OutsideButton"
        flTrackingLabel="CopyToClipboardClicked"
        [color]="ButtonColor.TEXT_DARK"
        [label]="'Copy to clipboard button'"
        [size]="ButtonSize.MID"
        (click)="copyToClipboard()"
        (mouseenter)="showTooltip(TooltipType.COPY)"
        (mouseleave)="tooltip.hide()"
      >
        <fl-icon
          [name]="'ui-multifile'"
          [size]="IconSize.SMALL"
          [useIconFont]="true"
          [fillIconFont]="true"
        ></fl-icon>
      </fl-button>
    </div>
  `,
  styleUrls: ['./clipboard-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClipboardFieldComponent implements OnInit {
  private clipboard = inject(Clipboard);
  private screenSizeService = inject(ScreenSizeService);
  private toastAlertService = inject(ToastAlertService);

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  ButtonTooltipText = buttonTooltipText;
  IconSize = IconSize;
  PASSWORD_MASK = PASSWORD_MASK;
  TooltipType = TooltipType;

  isVisible: boolean;
  isMobile$: Observable<boolean>;

  @Input() value = '';
  @Input() type: ClipboardFieldType = 'text';

  @ViewChild('tooltip') tooltip: MatTooltip;

  ngOnInit(): void {
    this.isMobile$ = this.screenSizeService.isMobile();
    this.isVisible = this.type !== 'password';
  }

  onCopyFieldClick(): void {
    const selectedText = this.clipboard.getSelectedText()?.toString();
    if (!selectedText) {
      this.copyToClipboard();
    }
  }

  /**
   * Handles copy event (ctrl-c or cmd-c) when part of the field is selected
   */
  onCopyFieldCopy(): void {
    const selectedText = this.clipboard.getSelectedText()?.toString();
    // Allow native copy if text is selected and visible or not a password field
    if (selectedText && (this.type !== 'password' || this.isVisible)) {
      return;
    }
    // Copy the entire value to clipboard
    this.copyToClipboard();
  }

  onVisibilityToggle(event: MouseEvent): void {
    event.stopPropagation();
    this.isVisible = !this.isVisible;
    this.showTooltip(this.isVisible ? TooltipType.HIDE : TooltipType.SHOW);
  }

  copyToClipboard(): void {
    this.clipboard.copy(this.value);
    this.toastAlertService.open({
      message: $localize`Successfully copied to clipboard.`,
      type: ToastAlertType.SUCCESS,
    });
  }

  showTooltip(tooltipType: TooltipType): void {
    this.tooltip.message = buttonTooltipText[tooltipType];
    this.tooltip.show();
  }
}
